import { useCallback, useEffect, useRef, useState } from 'react';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import {
	StoriesScrollNext,
	StoriesScrollPrev,
	StoriesScrollWrap,
	StoriesStrip,
} from '../storiesBar.styles';

const ScrollChevronLeft = () => (
	<svg viewBox="0 0 24 24" aria-hidden="true">
		<path
			d="m15 6-6 6 6 6"
			fill="none"
			stroke="currentColor"
			strokeWidth="2.2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const ScrollChevronRight = () => (
	<svg viewBox="0 0 24 24" aria-hidden="true">
		<path
			d="m9 6 6 6-6 6"
			fill="none"
			stroke="currentColor"
			strokeWidth="2.2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const StoriesBarStrip = ({ children, itemCount = 0 }) => {
	const t = useLanguageData('home');
	const stripRef = useRef(null);
	const [showPrev, setShowPrev] = useState(false);
	const [showNext, setShowNext] = useState(false);

	const updateScrollButtons = useCallback(() => {
		const strip = stripRef.current;

		if (!strip) {
			return;
		}

		const maxScroll = strip.scrollWidth - strip.clientWidth;
		const hasOverflow = maxScroll > 4;
		const isAtStart = strip.scrollLeft <= 4;
		const isAtEnd = strip.scrollLeft >= maxScroll - 4;

		setShowPrev(hasOverflow && !isAtStart);
		setShowNext(hasOverflow && !isAtEnd);
	}, []);

	useEffect(() => {
		updateScrollButtons();
		window.addEventListener('resize', updateScrollButtons);

		return () => {
			window.removeEventListener('resize', updateScrollButtons);
		};
	}, [updateScrollButtons, itemCount]);

	const scroll = (direction) => {
		const strip = stripRef.current;

		if (!strip) {
			return;
		}

		const amount = Math.max(280, Math.round(strip.clientWidth * 0.72));
		strip.scrollBy({ left: direction * amount, behavior: 'smooth' });
	};

	return (
		<StoriesScrollWrap $showPrevFade={showPrev} $showNextFade={showNext}>
			<StoriesScrollPrev
				type="button"
				$visible={showPrev}
				disabled={!showPrev}
				aria-label={t.previousStories}
				onClick={() => scroll(-1)}
			>
				<ScrollChevronLeft />
			</StoriesScrollPrev>
			<StoriesStrip ref={stripRef} onScroll={updateScrollButtons}>
				{children}
			</StoriesStrip>
			<StoriesScrollNext
				type="button"
				$visible={showNext}
				disabled={!showNext}
				aria-label={t.nextStories}
				onClick={() => scroll(1)}
			>
				<ScrollChevronRight />
			</StoriesScrollNext>
		</StoriesScrollWrap>
	);
};

export default StoriesBarStrip;
